/**
 * Utilities for converting between React Query keys and Next.js cache tags
 * This enables a unified caching strategy across client and server
 */

/**
 * Convert a React Query key to a Next.js cache tag string
 * @param queryKey - The React Query key array
 * @returns A string suitable for use as a Next.js cache tag
 * 
 * @example
 * queryKeyToTag(['shops', 'popular', { size: 21 }])
 * // Returns: "shops:popular:{"size":21}"
 */
export function queryKeyToTag(queryKey: readonly unknown[]): string {
  return queryKey
    .map(part => {
      if (typeof part === 'object' && part !== null) {
        // Sort object keys for consistent tag generation
        const sortedObj = Object.keys(part as Record<string, unknown>)
          .sort()
          .reduce((acc, key) => {
            acc[key] = (part as Record<string, unknown>)[key];
            return acc;
          }, {} as Record<string, unknown>);
        return JSON.stringify(sortedObj);
      }
      return String(part);
    })
    .join(':');
}

/**
 * Generate hierarchical cache tags for a query key
 * This allows invalidating parent queries when needed
 * 
 * @param queryKey - The React Query key array
 * @returns An array of cache tags from most general to most specific
 * 
 * @example
 * getHierarchicalTags(['shops', 'detail', 'abc123'])
 * // Returns: ["shops", "shops:detail", "shops:detail:abc123"]
 */
export function getHierarchicalTags(queryKey: readonly unknown[]): string[] {
  const tags: string[] = [];
  for (let i = 1; i <= queryKey.length; i++) {
    tags.push(queryKeyToTag(queryKey.slice(0, i)));
  }
  return tags;
}

/**
 * Helper to create tags for both specific and general invalidation
 * Useful when you want to cache with specific parameters but also
 * invalidate when the general resource changes
 * 
 * @param specificKey - The specific query key
 * @param generalKey - The general query key (optional, defaults to first element)
 * @returns An array of cache tags
 * 
 * @example
 * getCacheTags(shopQueryKeys.popular({ size: 21 }))
 * // Returns tags for both the specific query and "shops" general tag
 */
export function getCacheTags(
  specificKey: readonly unknown[],
  generalKey?: readonly unknown[]
): string[] {
  const tags = [queryKeyToTag(specificKey)];
  
  // Add the general tag (typically the resource name)
  if (generalKey) {
    tags.push(queryKeyToTag(generalKey));
  } else if (specificKey.length > 0) {
    // Default to using just the first element as general tag
    tags.push(queryKeyToTag([specificKey[0]]));
  }
  
  return tags;
}